import React, { useState, useMemo, useCallback, useRef } from "react";
import { Menu, Button, Checkbox, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";

const CustomMultiSelect = ({
	data = [],
	value = [],
	onChange,
	placeholder,
	disabled = false,
	label = "",
	size = "xs",
}) => {
	const [opened, setOpened] = useState(false);
	const [visibleCount, setVisibleCount] = useState(50);
	const [searchValue, setSearchValue] = useState("");
	const scrollRef = useRef(null);

	const handleToggle = useCallback(
		(itemValue) => {
			if (value.includes(itemValue)) {
				onChange(value.filter((v) => v !== itemValue));
			} else {
				onChange([...value, itemValue]);
			}
		},
		[value, onChange]
	);

	// Filtrar por búsqueda
	const filteredData = useMemo(() => {
		if (!searchValue.trim()) return data;
		
		return data.filter(item => {
			const itemLabel = (item.label || item).toString().toLowerCase();
			return itemLabel.includes(searchValue.toLowerCase());
		});
	}, [data, searchValue]);

	const handleScroll = useCallback(() => {
		if (scrollRef.current && visibleCount < filteredData.length) {
			const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
			if (scrollTop + clientHeight >= scrollHeight * 0.9) {
				setVisibleCount(prev => Math.min(prev + 25, filteredData.length));
			}
		}
	}, [visibleCount, filteredData.length]);

	const getSummaryText = () => {
		if (value.length === 0) return placeholder;
		return `${value.length} ${label} seleccionados`;
	};

	// Ordenar items filtrados
	const sortedData = useMemo(() => {
		const selected = [];
		const unselected = [];

		filteredData.forEach(item => {
			const itemValue = item.value || item;
			if (value.includes(itemValue)) {
				selected.push(item);
			} else {
				unselected.push(item);
			}
		});

		return [...selected, ...unselected];
	}, [filteredData, value]);

	// Scroll infinito 
	const displayedItems = useMemo(() => {
		return sortedData.slice(0, visibleCount);
	}, [sortedData, visibleCount]);

	const getDropdownWidth = useMemo(() => {
		if (!data.length) return "200px";

		const longestText = data.reduce((longest, item) => {
			const label = item.label || item;
			return label.length > longest.length ? label : longest;
		}, "");

		const estimatedWidth = Math.max(longestText.length * 8 + 80, 200);
		return `${Math.min(estimatedWidth, 400)}px`;
	}, [data]);

	return (
		<Menu
			opened={opened}
			onChange={(isOpen) => {
				setOpened(isOpen);
				if (!isOpen) {
					setSearchValue(""); 
					setVisibleCount(50); 
				}
			}}
			position="bottom-start"
			withArrow={false}
			shadow="md"
			width={getDropdownWidth}
			disabled={disabled}
			closeOnItemClick={false}
		>
			<Menu.Target>
				<Button
					variant="light"
					size={size}
					fullWidth
					disabled={disabled}
					onClick={() => setOpened(!opened)}
					className="btn  d-flex justify-content-start align-items-center custom-multiselect-button"
					style={{
						backgroundColor: value.length > 0 ? "var(--vz-primary)" : undefined,
						color: value.length > 0 ? "white" : undefined,
						borderColor: value.length > 0 ? "var(--vz-primary)" : undefined,
					}}
				>
					{getSummaryText()}
				</Button>
			</Menu.Target>

			<Menu.Dropdown
				className="shadow border custom-multiselect-dropdown"
				style={{
					width: "auto",
					minWidth: getDropdownWidth,
				}}
			>
				<div className="p-2 border-bottom">
					<TextInput
						placeholder={`Buscar ${label.toLowerCase()}...`}
						value={searchValue}
						onChange={(e) => {
							setSearchValue(e.target.value);
							setVisibleCount(50);
						}}
						size="xs"
						leftSection={<IconSearch size={14} />}
						onClick={(e) => e.stopPropagation()}
					/>
				</div>

				<div
					className="custom-multiselect-items"
					ref={scrollRef}
					onScroll={handleScroll}
				>
					{displayedItems.map((item) => {
						const itemValue = item.value || item;
						const itemLabel = item.label || item;
						const isChecked = value.includes(itemValue);

						return (
							<Menu.Item
								key={itemValue}
								onClick={(e) => {
									e.stopPropagation();
									handleToggle(itemValue);
								}}
								className={`d-flex align-items-center p-2 mb-1 custom-multiselect-item ${isChecked ? "selected" : ""}`}
							>
								<div className="d-flex align-items-center gap-2">
									<Checkbox
										checked={isChecked}
										onChange={() => { }}
										size="sm"
										style={{ pointerEvents: "none" }}
									/>
									<span className="small">{itemLabel}</span>
								</div>
							</Menu.Item>
						);
					})}

					{displayedItems.length === 0 && searchValue && (
						<Menu.Item disabled className="text-center p-3">
							<span className="text-muted small">
								No se encontraron coincidencias
							</span>
						</Menu.Item>
					)}

					{filteredData.length === 0 && !searchValue && (
						<Menu.Item disabled className="text-center p-3">
							<span className="text-muted small">
								No hay opciones disponibles
							</span>
						</Menu.Item>
					)}
				</div>
			</Menu.Dropdown>
		</Menu>
	);
};

export default CustomMultiSelect;
